import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import TopBar from './TopBar';
import { getPickupPointsByCondoId } from '../services/pickupPointService';
import * as foodItemService from '../services/foodItemService';
import { getPickupPointById } from '../services/pickupPointService';
import { createFoodImage } from '../services/foodImageService';
import { getFoodImages } from '../services/foodImageService';
import { getProfileByUserId } from '../services/profileService';
import { getAllResidences } from '../services/residenceService';
import ConfirmModal from './ConfirmModal';
import './SellFoodPage.css';

const SellFoodPage = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { showSuccess, showError, showInfo } = useToast();

  console.log('SellFoodPage - Auth Check:', {
    hasUser: !!user,
    hasProfile: !!profile,
    role: profile?.role
  });

  // Check authentication - redirect if not logged in
  if (!user) {
    console.log('No user - redirecting to login');
    return <Navigate to="/login" replace state={{
      message: 'Please log in to sell food',
      redirectTo: '/sell-food'
    }} />;
  }

  // Show loading while profile is being fetched
  if (!profile) {
    console.log('Profile not loaded yet, waiting...');
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#FBFFF0'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #2E7D32',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Check if user is a vendor - redirect if not
  if (profile.role !== 'vendor') {
    console.log('Not a vendor (role:', profile.role, ') - redirecting to vendor page');
    return <Navigate to="/vendor" replace state={{
      message: 'You need to be approved as a vendor to sell food'
    }} />;
  }

  console.log('User is a vendor - rendering page');

  // Helper to format a Date instance for the `datetime-local` input (local timezone, no seconds)
  const formatDateTimeLocal = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Default prefill: pickup will be N minutes after now to avoid immediate validation errors
  const DEFAULT_PREFILL_MINUTES = 30;
  const now = new Date();
  const cutoffPrefill = formatDateTimeLocal(now);
  const pickupPrefill = formatDateTimeLocal(new Date(now.getTime() + DEFAULT_PREFILL_MINUTES * 60_000));

  // Field max lengths
  const MAX_LENGTHS = {
    foodName: 50,
    description: 300,
    ingredients: 300
  };

  // Current local datetime (formatted for `datetime-local` inputs). Kept in state and updated periodically so `min` attributes stay fresh.
  const [currentDateTimeLocal, setCurrentDateTimeLocal] = useState(formatDateTimeLocal(new Date()));

  useEffect(() => {
    const update = () => setCurrentDateTimeLocal(formatDateTimeLocal(new Date()));
    const id = setInterval(update, 30_000); // update every 30 seconds to keep min accurate
    return () => clearInterval(id);
  }, []);

  // Helper that returns the current time rounded to minute precision (consistent with `datetime-local` inputs)
  const getNowRoundedDate = () => new Date(formatDateTimeLocal(new Date()));

  const [formData, setFormData] = useState({
    foodName: '',
    price: '',
    description: '',
    ingredients: '',
    mealTypes: {
      breakfast: false,
      lunch: false,
      dinner: false
    },
    dietaryLabels: {
      vegetarian: false
    },
    availability: 'available', // 'available' or 'preorder'
    orderCutoffTime: cutoffPrefill,
    maximumOrders: '',
    pickupTime: pickupPrefill,
    selectedCondominium: '',
    pickupLocation: '',
    isFavorite: false
  });

  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [prefilledImageIds, setPrefilledImageIds] = useState([]);
  const [condominiums, setCondominiums] = useState([]);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCondos, setLoadingCondos] = useState(true);
  const [loadingPickupPoints, setLoadingPickupPoints] = useState(false);

  // Favorite listings (max 10)
  const [favoriteListings, setFavoriteListings] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(false);

  // Replacement logic state
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [selectedReplacementId, setSelectedReplacementId] = useState(null);
  const [prefilledSourceId, setPrefilledSourceId] = useState(null);
  const [showSimilarModal, setShowSimilarModal] = useState(false);
  const [currentlyCreatedDraftId, setCurrentlyCreatedDraftId] = useState(null);

  // Minimum gap (in minutes) required between order cutoff time and pickup time.
  // Change DEFAULT_PICKUP_GAP_MINUTES to enforce a buffer (configurable later).
  const DEFAULT_PICKUP_GAP_MINUTES = 0; // set to 0 for now, change to required minutes (e.g., 15) later
  const [pickupGapMinutes, setPickupGapMinutes] = useState(DEFAULT_PICKUP_GAP_MINUTES);

  // Validation state for time gap
  const [timeError, setTimeError] = useState('');

  // Fetch all condominiums on component mount
  useEffect(() => {
    const fetchCondominiums = async () => {
      try {
        setLoadingCondos(true);
        const condos = await getAllResidences();
        setCondominiums(condos);

        // Auto-select user's condominium if available
        if (profile?.condo_id && condos.length > 0) {
          const userCondo = condos.find(c => c.id === profile.condo_id);
          if (userCondo) {
            setFormData(prev => ({
              ...prev,
              selectedCondominium: userCondo.id
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching condominiums:', error);
        showError(error, 'Failed to load condominiums. Please try again.');
      } finally {
        setLoadingCondos(false);
      }
    };

    fetchCondominiums();
  }, [profile]);

  // Fetch pickup points when condominium is selected
  useEffect(() => {
    const fetchPickupPoints = async () => {
      if (!formData.selectedCondominium) {
        setPickupPoints([]);
        setFormData(prev => ({ ...prev, pickupLocation: '' }));
        return;
      }

      try {
        setLoadingPickupPoints(true);
        const points = await getPickupPointsByCondoId(formData.selectedCondominium);
        setPickupPoints(points);
        // Reset pickup location when condominium changes
        setFormData(prev => ({ ...prev, pickupLocation: '' }));
      } catch (error) {
        console.error('Error fetching pickup points:', error);
        showError(error, 'Failed to load pickup locations. Please try again.');
      } finally {
        setLoadingPickupPoints(false);
      }
    };

    fetchPickupPoints();
  }, [formData.selectedCondominium]);

  // Fetch vendor's favorite listings (limit 10)
  const fetchFavorites = async () => {
    if (!user) return;
    try {
      setLoadingFavorites(true);
      const items = await foodItemService.getFavoriteFoodItems(user.$id);
      setFavoriteListings(items);
    } catch (error) {
      console.error('Error fetching favorite listings:', error);
    } finally {
      setLoadingFavorites(false);
    }
  };

  useEffect(() => {
    fetchFavorites();
  }, [user]);

  // Prefill form from a previous listing (keeps times untouched)
  const prefillFromListing = async (listing) => {
    if (!listing) return;
    try {
      // Fetch pickup point to determine condominium and ensure pickup location exists
      let condoId = '';
      if (listing.pickup_id) {
        try {
          const pickupPoint = await getPickupPointById(listing.pickup_id);
          condoId = pickupPoint.condoId || '';
        } catch (err) {
          console.warn('Could not fetch pickup point for prefill:', err);
        }
      }

      // Convert meal_type (Appwrite stores as array) to mealTypes object
      const mealTypesObj = {
        breakfast: false,
        lunch: false,
        dinner: false
      };
      if (Array.isArray(listing.meal_type)) {
        if (listing.meal_type.includes('breakfast') || listing.meal_type.includes('Breakfast')) mealTypesObj.breakfast = true;
        if (listing.meal_type.includes('lunch') || listing.meal_type.includes('Lunch')) mealTypesObj.lunch = true;
        if (listing.meal_type.includes('dinner') || listing.meal_type.includes('Dinner')) mealTypesObj.dinner = true;
      }

      const dietary = {
        vegetarian: Array.isArray(listing.dietary_label) ? listing.dietary_label.includes('Vegetarian') : (String(listing.dietary_label).toLowerCase() === 'vegetarian')
      };

      setFormData(prev => ({
        ...prev,
        foodName: listing.item_name || prev.foodName,
        price: listing.price != null ? String(listing.price) : prev.price,
        description: listing.description || prev.description,
        ingredients: listing.ingredients || prev.ingredients,
        mealTypes: mealTypesObj,
        dietaryLabels: dietary,
        availability: listing.is_preorder ? 'preorder' : 'available',
        maximumOrders: listing.stock_count != null ? String(listing.stock_count) : prev.maximumOrders,
        selectedCondominium: condoId || prev.selectedCondominium,
        isFavorite: false, // Changed from listing.favourite === 'True' to false by default
        // Intentionally do not modify orderCutoffTime or pickupTime
      }));

      // Track the source ID for specific replacement logic
      setPrefilledSourceId(listing.$id);
      setSelectedReplacementId(null); // Reset any previous replacement choice

      // If we have condoId, fetch pickup points and set pickupLocation if available
      if (condoId) {
        try {
          const points = await getPickupPointsByCondoId(condoId);
          setPickupPoints(points);
          const found = points.find(p => p.id === listing.pickup_id);
          if (found) {
            setFormData(prev => ({ ...prev, pickupLocation: listing.pickup_id }));
          }
        } catch (err) {
          console.warn('Error fetching pickup points during prefill:', err);
        }
      }

      showInfo('Form prefilled from previous listing (times left unchanged)');

      // Load any existing images for this listing and show previews without requiring upload
      try {
        // Clear all previous image states first
        setPrefilledImageIds([]);
        setImagePreviews([]);
        setImages([]);

        const imgs = await getFoodImages(listing.$id);
        if (imgs && imgs.length > 0) {
          const ids = imgs.map(i => i.imageId);
          setPrefilledImageIds(ids);
          const urls = ids.map(id => foodItemService.getFoodImageUrl(id)).filter(Boolean);
          setImagePreviews(urls);
        }
      } catch (err) {
        console.warn('Failed to load prefilled images for listing:', err);
        setPrefilledImageIds([]);
        setImagePreviews([]);
        setImages([]);
      }
    } catch (error) {
      console.error('Error pre-filling from listing:', error);
      showError('Failed to prefill from previous listing');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
      return;
    }

    // Enforce maximum lengths for text fields (defensive in case browser doesn't enforce maxlength)
    let newValue = value;
    const max = MAX_LENGTHS[name];
    if (typeof max !== 'undefined' && value.length > max) {
      newValue = value.slice(0, max);
    }

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Validate time gap when cutoff or pickup fields change
    if (name === 'orderCutoffTime' || name === 'pickupTime') {
      const cutoff = name === 'orderCutoffTime' ? newValue : formData.orderCutoffTime;
      const pickup = name === 'pickupTime' ? newValue : formData.pickupTime;
      const message = validateTimeGap(cutoff, pickup);
      setTimeError(message);
    }
  };

  // Prevent number inputs from changing on scroll (mouse wheel / touchpad)
  const handleNumberWheel = (e) => {
    // If the number input is focused, blur it so wheel events don't increment/decrement the value
    try {
      if (e && e.currentTarget) {
        e.currentTarget.blur();
      }
      e.preventDefault();
      e.stopPropagation();
    } catch (err) {
      // Fallback - do nothing
      console.warn('handleNumberWheel warning:', err);
    }
  };

  const handleMealTypeChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      mealTypes: {
        ...prev.mealTypes,
        [name]: checked
      }
    }));
  };

  const handleDietaryLabelChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      dietaryLabels: {
        ...prev.dietaryLabels,
        [name]: checked
      }
    }));
  };

  const handleAvailabilityChange = (availability) => {
    setFormData(prev => ({
      ...prev,
      availability
    }));
  };

  // Validate that pickup is after cutoff and satisfies the required gap
  const validateTimeGap = (cutoffStr, pickupStr) => {
    if (!cutoffStr || !pickupStr) return '';

    const pickupDate = new Date(pickupStr);
    const cutoffDate = new Date(cutoffStr);

    if (isNaN(pickupDate.getTime()) || isNaN(cutoffDate.getTime())) {
      return 'Please enter valid date and time values for pickup and cutoff';
    }

    // Ensure neither time is earlier than current time (rounded to minute precision)
    const nowRounded = getNowRoundedDate();
    if (cutoffDate.getTime() < nowRounded.getTime()) {
      return 'Order cutoff cannot be earlier than the current time';
    }
    if (pickupDate.getTime() < nowRounded.getTime()) {
      return 'Pickup time cannot be earlier than the current time';
    }

    const diffMinutes = (pickupDate.getTime() - cutoffDate.getTime()) / (1000 * 60);

    if (diffMinutes <= 0) {
      return 'Pickup time must be after cutoff time';
    }

    if (diffMinutes < pickupGapMinutes) {
      return `Pickup time must be at least ${pickupGapMinutes} minute(s) after cutoff time`;
    }

    return '';
  };

  // Keep validation state up-to-date when times or gap change
  useEffect(() => {
    const msg = validateTimeGap(formData.orderCutoffTime, formData.pickupTime);
    setTimeError(msg);
  }, [formData.orderCutoffTime, formData.pickupTime, pickupGapMinutes]);

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + images.length + prefilledImageIds.length > 3) {
      showError('Maximum 3 images allowed (Total of 3 across existing and new)');
      return;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const isValidType = validTypes.includes(file.type);
      const isValidSize = file.size <= maxSize;

      if (!isValidType) {
        showError(`${file.name}: Only JPG, JPEG, and PNG formats are allowed`);
        return false;
      }

      if (!isValidSize) {
        showError(`${file.name}: File size must be less than 10MB (current: ${(file.size / 1024 / 1024).toFixed(2)}MB)`);
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;

    // Do NOT clear prefilledImageIds. We want to support mixing.
    setImages(prev => [...prev, ...validFiles]);

    // Create previews
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);

    if (files.length + images.length + prefilledImageIds.length > 3) {
      showError('Maximum 3 images allowed (Total of 3 across existing and new)');
      return;
    }

    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const isValidType = validTypes.includes(file.type);
      const isValidSize = file.size <= maxSize;

      if (!isValidType) {
        showError(`${file.name}: Only JPG, JPEG, and PNG formats are allowed`);
        return false;
      }

      if (!isValidSize) {
        showError(`${file.name}: File size must be less than 10MB (current: ${(file.size / 1024 / 1024).toFixed(2)}MB)`);
        return false;
      }

      return true;
    });

    if (validFiles.length === 0) return;

    // Do NOT clear prefilledImageIds
    setImages(prev => [...prev, ...validFiles]);

    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    // imagePreviews contains [prefilledUrl1, prefilledUrl2..., newFileUrl1, newFileUrl2...]
    // Check if the removed index belongs to a prefilled image or a newly added image
    if (index < prefilledImageIds.length) {
      // Removing a pre-filled image ID
      setPrefilledImageIds(prev => prev.filter((_, i) => i !== index));
    } else {
      // Removing a newly added File object
      const relativeIndex = index - prefilledImageIds.length;
      setImages(prev => prev.filter((_, i) => i !== relativeIndex));
    }

    // Always remove from previews
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate user is authenticated
    if (!user) {
      showInfo('You must be logged in to create a food listing');
      navigate('/login');
      return;
    }

    // Validate required fields
    if (!formData.foodName || !formData.price || !formData.ingredients) {
      showError('Please fill in all required fields');
      return;
    }

    // Enforce maximum lengths
    if (formData.foodName.length > MAX_LENGTHS.foodName) {
      showError(`Food name must be at most ${MAX_LENGTHS.foodName} characters`);
      return;
    }
    if (formData.description && formData.description.length > MAX_LENGTHS.description) {
      showError(`Description must be at most ${MAX_LENGTHS.description} characters`);
      return;
    }
    if (formData.ingredients.length > MAX_LENGTHS.ingredients) {
      showError(`Ingredients must be at most ${MAX_LENGTHS.ingredients} characters`);
      return;
    }

    if (!formData.mealTypes.breakfast && !formData.mealTypes.lunch && !formData.mealTypes.dinner) {
      showError('Please select at least one meal type');
      return;
    }

    if (!formData.selectedCondominium) {
      showError('Please select a condominium');
      return;
    }

    if (!formData.orderCutoffTime || !formData.maximumOrders || !formData.pickupTime || !formData.pickupLocation) {
      showError('Please fill in all order and pickup details');
      return;
    }

    // Validate datetime formats and logical order (cutoff must be before pickup + gap)
    const timeValidationMsg = validateTimeGap(formData.orderCutoffTime, formData.pickupTime);
    if (timeValidationMsg) {
      showError(timeValidationMsg);
      return;
    }

    // Final validation: ensure image count does not exceed 3 (defensive)
    if (images.length > 3) {
      showError('Maximum 3 images allowed');
      return;
    }

    // Check favorite limit (max 10)
    if (formData.isFavorite && !selectedReplacementId && favoriteListings.length >= 10) {
      // This is a safety check in case the modal didn't trigger
      setShowReplaceModal(true);
      return;
    }

    // Proceed with normal submission
    await performSubmit(selectedReplacementId);
  };

  const performImmediateFavoriteSave = async (replacedId = null) => {
    try {
      setLoadingFavorites(true);
      const mealTypeArr = Object.keys(formData.mealTypes).filter(k => formData.mealTypes[k]);
      const dietaryLabelArr = formData.dietaryLabels.vegetarian ? ['Vegetarian'] : [];

      if (replacedId) {
        await foodItemService.updateFavoriteStatus(replacedId, 'False');
        console.log('Previous favorite unpinned:', replacedId);
      }

      const draftData = {
        pickupId: formData.pickupLocation || null,
        itemName: formData.foodName,
        price: formData.price || '0',
        description: formData.description || '',
        ingredients: formData.ingredients || '',
        mealType: mealTypeArr,
        dietaryLabel: dietaryLabelArr,
        stockCount: formData.maximumOrders || '0',
        pickupTime: formData.pickupTime || new Date().toISOString(),
        cutoffTime: formData.orderCutoffTime || new Date().toISOString(),
        isPreorder: formData.availability === 'preorder',
        favourite: 'True',
        hiddenAfter: '1970-01-01T00:00:00Z',
        listed: 'False'
      };

      const created = await foodItemService.createFoodItem(draftData, user.$id);
      setCurrentlyCreatedDraftId(created.$id);
      setFormData(prev => ({ ...prev, isFavorite: true }));
      await fetchFavorites();
      showSuccess(replacedId ? 'Favorite replaced and updated!' : 'Saved as favorite!');
    } catch (err) {
      console.error('Immediate favorite save failed:', err);
      showError('Failed to save to favorites list.  Please complete all required fields.');
    } finally {
      setLoadingFavorites(false);
    }
  };

  const performSubmit = async (replaceId = null) => {
    try {
      setLoading(true);
      if (showReplaceModal) setShowReplaceModal(false);

      // Upload all images to storage
      const uploadedImageIds = [];
      if (images.length > 0) {
        try {
          console.log(`Uploading ${images.length} images...`);
          for (let i = 0; i < images.length; i++) {
            const image = images[i];
            console.log(`Uploading image ${i + 1}/${images.length}:`, image.name, 'Size:', image.size, 'Type:', image.type);
            const imageId = await foodItemService.uploadFoodImage(image);
            uploadedImageIds.push(imageId);
            console.log(`Image ${i + 1} uploaded successfully with ID:`, imageId);
          }
          console.log('All images uploaded successfully:', uploadedImageIds);
        } catch (error) {
          console.error('Error uploading images:', error);
          console.error('Error details:', error.message, error.code, error.type);

          let errorMessage = 'Failed to upload images. ';
          if (error.code === 401) {
            errorMessage += 'Please log in again.';
          } else if (error.code === 413) {
            errorMessage += 'Image file is too large.';
          } else if (error.message) {
            errorMessage += error.message;
          } else {
            errorMessage += 'Please check your internet connection and try again.';
          }

          showError(errorMessage);
          setLoading(false);
          return;
        }
      }

      // Determine meal type (combine all selected meal types)
      const selectedMealTypes = [];
      if (formData.mealTypes.breakfast) selectedMealTypes.push('breakfast');
      if (formData.mealTypes.lunch) selectedMealTypes.push('lunch');
      if (formData.mealTypes.dinner) selectedMealTypes.push('dinner');
      const mealType = selectedMealTypes; // Send as an array to match updated schema

      // Determine dietary label - send as an array of strings (Appwrite Enum is capitalized: 'Vegetarian')
      const selectedDietaryLabels = [];
      if (formData.dietaryLabels.vegetarian) selectedDietaryLabels.push('Vegetarian');
      const dietaryLabel = selectedDietaryLabels;

      // Create ISO datetime strings for pickup_time and cutoff_time from datetime-local inputs
      const pickupDateObj = new Date(formData.pickupTime);
      if (isNaN(pickupDateObj.getTime())) throw new Error('Invalid pickup date/time');
      const pickupDateTime = pickupDateObj.toISOString();

      const cutoffDateObj = new Date(formData.orderCutoffTime);
      if (isNaN(cutoffDateObj.getTime())) throw new Error('Invalid cutoff date/time');
      const cutoffDateTime = cutoffDateObj.toISOString();

      // Prepare food item data (without imageId - images are in separate collection)
      const foodItemData = {
        pickupId: formData.pickupLocation,
        itemName: formData.foodName,
        price: formData.price,
        description: formData.description,
        ingredients: formData.ingredients,
        mealType: mealType,
        dietaryLabel: dietaryLabel,
        stockCount: formData.maximumOrders,
        pickupTime: pickupDateTime,
        isPreorder: formData.availability === 'preorder',
        cutoffTime: cutoffDateTime
      };

      // Create or update food item in database
      let createdFoodItem;
      if (currentlyCreatedDraftId) {
        console.log('Publishing existing favorite draft:', currentlyCreatedDraftId);
        createdFoodItem = await foodItemService.updateFoodItem(currentlyCreatedDraftId, {
          pickup_id: foodItemData.pickupId || null,
          item_name: foodItemData.itemName,
          price: isNaN(parseFloat(foodItemData.price)) ? 0 : parseFloat(foodItemData.price),
          description: foodItemData.description || '',
          ingredients: foodItemData.ingredients || '',
          meal_type: foodItemData.mealType,
          dietary_label: foodItemData.dietaryLabel || 'None',
          stock_count: isNaN(parseInt(foodItemData.stockCount)) ? 0 : parseInt(foodItemData.stockCount),
          pickup_time: foodItemData.pickupTime,
          is_preorder: foodItemData.isPreorder,
          cutoff_time: foodItemData.cutoffTime,
          favourite: formData.isFavorite ? 'True' : 'False',
          hidden_after: null,
          listed: 'True'
        });
      } else {
        createdFoodItem = await foodItemService.createFoodItem({
          ...foodItemData,
          favourite: formData.isFavorite ? 'True' : 'False',
          hiddenAfter: null,
          listed: 'True'
        }, user.$id);
      }
      console.log('Food item created/published:', createdFoodItem);

      // If we replaced a favorite, update the old one to 'False'
      if (replaceId) {
        try {
          await foodItemService.updateFavoriteStatus(replaceId, 'False');
          console.log('Previous favorite unpinned:', replaceId);
        } catch (error) {
          console.warn('Failed to unpin previous favorite:', error);
        }
      }

      // Create FoodImage records for all uploaded images
      if (uploadedImageIds.length > 0) {
        try {
          console.log(`Creating ${uploadedImageIds.length} FoodImage records for food item:`, createdFoodItem.$id);
          for (const imageId of uploadedImageIds) {
            console.log('Creating FoodImage record with food_id:', createdFoodItem.$id, 'image_id:', imageId);
            await createFoodImage(createdFoodItem.$id, imageId);
            console.log('FoodImage record created successfully for imageId:', imageId);
          }
          console.log('All FoodImage records created successfully');
        } catch (error) {
          console.error('Error creating FoodImage records:', error);
          console.error('Error details:', {
            message: error.message,
            code: error.code,
            type: error.type,
            response: error.response
          });

          // Food item is already created, so we can continue
          let errorMessage = 'Food item created, but there was an issue linking images. ';
          if (error.code === 401) {
            errorMessage += 'Authentication error - please try logging in again.';
          } else if (error.message) {
            errorMessage += error.message;
          }

          showError(errorMessage);
          // Don't return - still show success and reset form
        }
      }

      // If the prefilled listing had images, attach those image IDs as well
      if (prefilledImageIds && prefilledImageIds.length > 0) {
        try {
          console.log(`Attaching ${prefilledImageIds.length} prefilled images to food item:`, createdFoodItem.$id);
          for (const imageId of prefilledImageIds) {
            console.log('Linking prefilled image:', imageId);
            await createFoodImage(createdFoodItem.$id, imageId);
          }
          console.log('All prefilled images linked successfully');
        } catch (error) {
          console.error('Error linking prefilled images:', error);
          // Non-fatal - inform user but continue
          showError('Food item created, but could not attach some prefilled images.');
        }
      }

      showSuccess('Food item listing submitted successfully! ✓');

      // Reset form (prefill times again to current local time)
      const nowReset = new Date();
      const cutoffReset = formatDateTimeLocal(nowReset);
      const pickupReset = formatDateTimeLocal(new Date(nowReset.getTime() + DEFAULT_PREFILL_MINUTES * 60_000));

      setFormData({
        foodName: '',
        price: '',
        description: '',
        ingredients: '',
        mealTypes: {
          breakfast: false,
          lunch: false,
          dinner: false
        },
        dietaryLabels: {
          vegetarian: false
        },
        availability: 'available',
        orderCutoffTime: cutoffReset,
        maximumOrders: '',
        pickupTime: pickupReset,
        selectedCondominium: '',
        pickupLocation: ''
      });
      setImages([]);
      setImagePreviews([]);
      setPrefilledImageIds([]);
      setPrefilledSourceId(null);
      setSelectedReplacementId(null);
      setCurrentlyCreatedDraftId(null);

      // Refresh favorites list
      await fetchFavorites();

      // Navigate to vendor dashboard or buy food page
      navigate('/buy-food');
    } catch (error) {
      console.error('Error creating food item:', error);
      showError(error, 'Failed to create food listing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sell-food-page">
      <TopBar />
      <div className="sell-food-container">
        <div className="sell-food-content">
          <h1>Add a New Food Item</h1>
          <p className="subtitle">Share your delicious creations with your community. Fill out the details below to list your food item.</p>

          {/* Favorite Listings Preview */}
          <div className="form-section favorite-listings">
            <div className="section-header">
              <span className="section-icon">⭐</span>
              <h2>Favorite Listings</h2>
              <div className="tooltip-container" aria-label="Favorite listings info">
                <div
                  className="tooltip-icon"
                  tabIndex={0}
                  role="button"
                  aria-describedby="favorite-tooltip"
                >
                  ?
                </div>
                <div className="tooltip-popup" id="favorite-tooltip">
                  Favorite your listings below to quickly pre-fill your listing form (Up to 10 listings can be favorited).
                </div>
              </div>
            </div>
            {loadingFavorites ? (
              <p>Loading favorite listings...</p>
            ) : (
              <>
                {favoriteListings.length > 0 ? (
                  <div className="favorite-listings-chips">
                    {favoriteListings.map((listing, idx) => (
                      <button
                        type="button"
                        key={idx}
                        className="favorite-chip"
                        onClick={() => prefillFromListing(listing)}
                      >
                        {listing.item_name}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="info-message">No favorite listings found. Mark a listing as favorite to see it here.</p>
                )}
                <div className="favorites-counter">
                  {favoriteListings.length}/10 favorites used
                </div>
              </>
            )}
          </div>

          <form onSubmit={handleSubmit}>
            {/* Food Images Section */}
            <div className="form-section">
              <div className="section-header">
                <div className="header-left">
                  <span className="section-icon">📷</span>
                  <h2>Food Images</h2>
                </div>
              </div>

              <div
                className="image-upload-area"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <div className="upload-icon">☁️</div>
                <p>Drag and drop your images here, or</p>
                {images.length >= 3 ? (
                  <span className="select-files-btn disabled">Maximum images reached</span>
                ) : (
                  <label htmlFor="image-input" className="select-files-btn">
                    Select Files
                  </label>
                )}
                <input
                  id="image-input"
                  type="file"
                  accept=".jpg,.jpeg,.png"
                  multiple
                  onChange={handleImageSelect}
                  disabled={images.length >= 3}
                  style={{ display: 'none' }}
                />
                <p className="upload-hint">Maximum 3 images, JPG or PNG format</p>
              </div>

              <div className="image-previews">
                {imagePreviews.map((preview, index) => (
                  <div key={index} className="image-preview-item" onClick={() => setPreviewImage(preview)}>
                    <img src={preview} alt={`Preview ${index + 1}`} />
                    <button
                      type="button"
                      className="remove-image-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeImage(index);
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
                {imagePreviews.length < 3 && (
                  <label htmlFor="image-input" className="add-more-images">
                    <div className="plus-icon">+</div>
                  </label>
                )}
              </div>
            </div>

            {/* Image Preview Modal */}
            {previewImage && (
              <div className="image-preview-modal" onClick={() => setPreviewImage(null)}>
                <div className="modal-content" onClick={e => e.stopPropagation()}>
                  <button className="modal-close-btn" onClick={() => setPreviewImage(null)}>×</button>
                  <img src={previewImage} alt="Full size preview" />
                </div>
              </div>
            )}

            {/* Favorite Replacement Modal */}
            {showReplaceModal && (
              <div className="replace-favorite-modal">
                <div className="modal-content">
                  <h3>Favorite Limit Reached</h3>
                  <p>You can only have 10 favorites. Please select one to replace with this new listing:</p>

                  <div className="replacement-options">
                    {favoriteListings.map(fav => (
                      <label key={fav.$id} className={`replacement-option ${selectedReplacementId === fav.$id ? 'selected' : ''}`}>
                        <input
                          type="radio"
                          name="replacement"
                          value={fav.$id}
                          onChange={() => setSelectedReplacementId(fav.$id)}
                        />
                        <div className="option-info">
                          <span className="option-name">{fav.item_name}</span>
                          <span className="option-price">RM {fav.price.toFixed(2)}</span>
                        </div>
                      </label>
                    ))}
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="cancel-btn"
                      onClick={() => setShowReplaceModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="confirm-replace-btn"
                      disabled={!selectedReplacementId}
                      onClick={async () => {
                        setShowReplaceModal(false);
                        await performImmediateFavoriteSave(selectedReplacementId);
                      }}
                    >
                      Confirm Choice
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Similar Favorite Detection Modal */}
            {showSimilarModal && (
              <div className="replace-favorite-modal">
                <div className="modal-content">
                  <h3>Similar Favorite Detected</h3>
                  <p>You already have a similar listing favourited. Proceeding would replace the previous favourited listing with this new one.</p>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="cancel-btn"
                      onClick={() => {
                        setShowSimilarModal(false);
                        setSelectedReplacementId(null);
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="confirm-replace-btn"
                      onClick={async () => {
                        setShowSimilarModal(false);
                        await performImmediateFavoriteSave(selectedReplacementId);
                      }}
                    >
                      Confirm Replacement
                    </button>
                  </div>
                </div>
              </div>
            )}


            {/* Food Details Section */}
            <div className="form-section">
              <div className="section-header">
                <span className="section-icon">📋</span>
                <h2>Food Details</h2>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="foodName">Food Name <span className="required">*</span></label>
                  <input
                    type="text"
                    id="foodName"
                    name="foodName"
                    placeholder="Enter food name"
                    value={formData.foodName}
                    onChange={handleInputChange}
                    maxLength={MAX_LENGTHS.foodName}
                    required
                  />
                  <small className={`char-counter ${formData.foodName.length >= MAX_LENGTHS.foodName ? 'maxed' : ''}`}>{formData.foodName.length}/{MAX_LENGTHS.foodName}</small>
                </div>

                <div className="form-group">
                  <label htmlFor="price">Price <span className="required">*</span></label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={handleInputChange}
                    onWheel={handleNumberWheel}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="ingredients">Ingredients <span className="required">*</span></label>
                <textarea
                  id="ingredients"
                  name="ingredients"
                  placeholder="List ingredients separated by commas..."
                  rows="3"
                  value={formData.ingredients}
                  onChange={handleInputChange}
                  maxLength={MAX_LENGTHS.ingredients}
                  required
                />
                <small className={`char-counter ${formData.ingredients.length >= MAX_LENGTHS.ingredients ? 'maxed' : ''}`}>{formData.ingredients.length}/{MAX_LENGTHS.ingredients}</small>
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  placeholder="Describe your food item..."
                  rows="4"
                  value={formData.description}
                  onChange={handleInputChange}
                  maxLength={MAX_LENGTHS.description}
                />
                <small className={`char-counter ${formData.description.length >= MAX_LENGTHS.description ? 'maxed' : ''}`}>{formData.description.length}/{MAX_LENGTHS.description}</small>
              </div>

              <div className="form-group">
                <div className="form-label">Meal Type <span className="required">*</span></div>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="breakfast"
                      checked={formData.mealTypes.breakfast}
                      onChange={handleMealTypeChange}
                    />
                    <span>Breakfast</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="lunch"
                      checked={formData.mealTypes.lunch}
                      onChange={handleMealTypeChange}
                    />
                    <span>Lunch</span>
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="dinner"
                      checked={formData.mealTypes.dinner}
                      onChange={handleMealTypeChange}
                    />
                    <span>Dinner</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <div className="form-label">Dietary Labels</div>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="vegetarian"
                      checked={formData.dietaryLabels.vegetarian}
                      onChange={handleDietaryLabelChange}
                    />
                    <span>Vegetarian</span>
                  </label>
                </div>
              </div>

              <div className="form-group">
                <div className="form-label">Availability</div>
                <div className="availability-buttons">
                  <button
                    type="button"
                    className={`availability-btn ${formData.availability === 'available' ? 'active' : ''}`}
                    onClick={() => handleAvailabilityChange('available')}
                  >
                    <span className="btn-icon">✓</span> Available Now
                  </button>
                  <button
                    type="button"
                    className={`availability-btn ${formData.availability === 'preorder' ? 'active' : ''}`}
                    onClick={() => handleAvailabilityChange('preorder')}
                  >
                    <span className="btn-icon">📋</span> Pre-orders Only
                  </button>
                </div>
              </div>
            </div>

            {/* Order & Pickup Details Section */}
            <div className="form-section">
              <div className="section-header">
                <span className="section-icon">🕒</span>
                <h2>Order & Pickup Details</h2>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="orderCutoffTime">Order Cutoff Time <span className="required">*</span></label>
                  <input
                    type="datetime-local"
                    id="orderCutoffTime"
                    name="orderCutoffTime"
                    value={formData.orderCutoffTime}
                    onChange={handleInputChange}
                    min={currentDateTimeLocal}
                    required
                  />
                  <small>Last date and time customers can place an order</small>
                  <small>Cannot be earlier than the current date/time</small>
                  <label htmlFor="maximumOrders">Maximum Orders <span className="required">*</span></label>
                  <input
                    type="number"
                    id="maximumOrders"
                    name="maximumOrders"
                    placeholder="e.g., 20"
                    min="1"
                    value={formData.maximumOrders}
                    onChange={handleInputChange}
                    onWheel={handleNumberWheel}
                    required
                  />
                  <small>Total number of orders you can fulfill</small>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="pickupTime">Pickup Time <span className="required">*</span></label>
                <input
                  type="datetime-local"
                  id="pickupTime"
                  name="pickupTime"
                  value={formData.pickupTime}
                  onChange={handleInputChange}
                  min={currentDateTimeLocal}
                  required
                />
                <small>Cannot be earlier than the current date/time</small>
                {pickupGapMinutes > 0 && (
                  <small>Pickup must be at least {pickupGapMinutes} minute(s) after the cutoff time.</small>
                )}
                {timeError && (
                  <p className="error-message">{timeError}</p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="selectedCondominium">Condominium <span className="required">*</span></label>
                {loadingCondos ? (
                  <p>Loading condominiums...</p>
                ) : condominiums.length > 0 ? (
                  <select
                    id="selectedCondominium"
                    name="selectedCondominium"
                    value={formData.selectedCondominium}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a condominium</option>
                    {condominiums.map((condo) => (
                      <option key={condo.id} value={condo.id}>
                        {condo.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="no-pickup-message">
                    No condominiums available. Please contact support.
                  </p>
                )}
                <small>Select the condominium where you'll sell your food</small>
              </div>

              <div className="form-group">
                <div className="form-label">Pickup Location <span className="required">*</span></div>
                {!formData.selectedCondominium ? (
                  <p className="info-message">Please select a condominium first</p>
                ) : loadingPickupPoints ? (
                  <p>Loading pickup locations...</p>
                ) : pickupPoints.length > 0 ? (
                  <select
                    name="pickupLocation"
                    value={formData.pickupLocation}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select a pickup location</option>
                    {pickupPoints.map((point) => (
                      <option key={point.id} value={point.id}>
                        {point.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="no-pickup-message">
                    No pickup locations available for this condominium.
                  </p>
                )}
                <small>Select your preferred pickup point</small>
              </div>
            </div>

            <div className="favorite-save-container">
              <button
                type="button"
                className={`favorite-save-btn ${formData.isFavorite ? 'active' : ''}`}
                onClick={async (e) => {
                  e.preventDefault();
                  if (formData.isFavorite) {
                    setFormData(prev => ({ ...prev, isFavorite: false }));
                    if (currentlyCreatedDraftId) {
                      try {
                        await foodItemService.deleteFoodItem(currentlyCreatedDraftId);
                        setCurrentlyCreatedDraftId(null);
                        await fetchFavorites();
                      } catch (err) {
                        console.warn('Failed to delete un-favorited draft:', err);
                      }
                    }
                    setSelectedReplacementId(null);
                  } else {
                    if (!formData.foodName) {
                      showError('Please enter a food name before saving as favorite');
                      return;
                    }

                    // Check if name matches ANY existing favorite
                    const duplicate = favoriteListings.find(
                      f => f.item_name.trim().toLowerCase() === formData.foodName.trim().toLowerCase()
                    );

                    if (duplicate) {
                      setSelectedReplacementId(duplicate.$id);
                      setShowSimilarModal(true);
                      return;
                    }

                    if (favoriteListings.length >= 10) {
                      setShowReplaceModal(true);
                      return;
                    }

                    // Immediate Save as draft
                    await performImmediateFavoriteSave();
                  }
                }}
              >
                <span className="heart-icon">{formData.isFavorite ? '❤️' : '🤍'}</span>
                {formData.isFavorite ? 'Saved as Favorite' : 'Save as Favorite'}
              </button>
            </div>

            <button type="submit" className="submit-btn" disabled={loading || !!timeError}>
              <span className="btn-icon">+</span> {loading ? 'Submitting...' : 'Submit Listing'}
            </button>

            <p className="terms-text">
              By submitting this listing, you agree to our <a href="/terms">Terms of Service</a>
            </p>
          </form>
        </div>
      </div >

    </div >
  );
};

export default SellFoodPage;
